import React, { useState, useEffect } from "react";
import ModalPopup from "./CommonComponents/ModalPopup";
import { FaTrash } from "react-icons/fa";
import { FiEdit2} from 'react-icons/fi';
import { fetchGstGroups, createGstGroups, updateGstGroups, deleteGstGroups } from "../services/ClientService";

function validateGST(form) {
  const errors = {};
  
  // Helper function to validate decimal with one decimal place
  const isValidDecimal = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    // Check if value has at most 1 decimal place
    const decimalPart = value.toString().split('.')[1];
    return decimalPart === undefined || decimalPart.length <= 1;
  };

  if (!form.name.trim()) errors.name = "Group name is required.";
  
  if (form.percentage === "" || isNaN(form.percentage) || !isValidDecimal(form.percentage) || form.percentage < 0 || form.percentage > 100) {
    errors.percentage = "GST % must be between 0 and 100 with at most 1 decimal place.";
  }
  
  if (form.cgst === "" || isNaN(form.cgst) || !isValidDecimal(form.cgst) || form.cgst < 0 || form.cgst > 100) {
    errors.cgst = "CGST is required and must be 0-100 with at most 1 decimal place.";
  }
  
  if (form.sgst === "" || isNaN(form.sgst) || !isValidDecimal(form.sgst) || form.sgst < 0 || form.sgst > 100) {
    errors.sgst = "SGST is required and must be 0-100 with at most 1 decimal place.";
  }
  
  if (form.igst === "" || isNaN(form.igst) || !isValidDecimal(form.igst) || form.igst < 0 || form.igst > 100) {
    errors.igst = "IGST is required and must be 0-100 with at most 1 decimal place.";
  }
  
  // Indian GST: CGST + SGST = GST percentage, IGST = CGST + SGST
  const cgst = parseFloat(form.cgst) || 0;
  const sgst = parseFloat(form.sgst) || 0;
  const igst = parseFloat(form.igst) || 0;
  const gst = parseFloat(form.percentage) || 0;
  
  if ((form.cgst !== "" && form.sgst !== "") && Math.abs(cgst + sgst - gst) > 0.01) {
    errors.cgst = errors.sgst = "CGST + SGST should equal GST %.";
  }
  
  if (form.cgst !== "" && form.sgst !== "" && form.igst !== "" && Math.abs(igst - (cgst + sgst)) > 0.01) {
    errors.igst = "IGST should be equal to CGST + SGST.";
  }
  
  return errors;
}

// Utility to flatten GST groups from API response
function flattenGstGroups(res) {
  let allGroups = [];
  if (Array.isArray(res?.data)) {
    res.data.forEach(item => {
      if (Array.isArray(item.gstGroups)) {
        allGroups = allGroups.concat(item.gstGroups.map(g => ({ ...g, parentId: item._id, metaData: item.metaData })));
      }
    });
  }
  return allGroups;
}

const GST = () => {
  const [groups, setGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({
    name: "",
    percentage: "",
    type: "inclusive",
    cgst: "",
    sgst: "",
    igst: ""
  });
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  // Fetch GST groups from backend
  const fetchAndSetGroups = async () => {
    setLoading(true);
    try {
      const res = await fetchGstGroups();
      setGroups(flattenGstGroups(res));
    } catch (err) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndSetGroups();
    // eslint-disable-next-line
  }, []);

  // Create or update GST group
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateGST(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setLoading(true);
    try {
      if (editingGroup !== null) {
        // For update, send gstGroup with _id if present
        await updateGstGroups({ gstGroup: form });
      } else {
        await createGstGroups({ gstGroup: form });
      }
      // Refetch all groups after create/update
      await fetchAndSetGroups();
      setShowForm(false);
      setEditingGroup(null);
      setForm({ name: "", percentage: "", type: "inclusive", cgst: "", sgst: "", igst: "" });
      setErrors({});
    } catch (err) {
      // Optionally show error
    } finally {
      setLoading(false);
    }
  };

  // Edit GST group
  const handleEdit = (idx) => {
    setEditingGroup(idx);
    setForm(groups[idx]);
    setShowForm(true);
    setErrors({});
  };

  // Delete GST group
  const handleDeleteClick = (idx) => {
    setDeleteIndex(idx);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteIndex === null) return;
    setLoading(true);
    try {
      // Send the group object to deleteGstGroups
      await deleteGstGroups({ gstGroup: groups[deleteIndex] });
      // Refetch all groups after delete
      await fetchAndSetGroups();
      setShowDeleteConfirm(false);
      setDeleteIndex(null);
    } catch (err) {
      // Optionally show error
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteIndex(null);
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">GST Groups</h2>
        <button
          className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400"
          onClick={() => {
            setShowForm(true);
            setEditingGroup(null);
            setForm({ name: "", percentage: "", type: "inclusive", cgst: "", sgst: "", igst: "" });
            setErrors({});
          }}
        >
          + Create GST Group
        </button>
      </div>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow p-4 mb-6 border border-gray-100 dark:border-gray-700">
        <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">GST (Goods and Services Tax) in India is a value-added tax applied on most goods and services. You can manage GST groups here for different product categories or scenarios. Each group can be inclusive or exclusive, and you can specify CGST, SGST, and IGST as per Indian tax rules.</p>
        <ul className="list-disc pl-5 text-xs text-gray-500 dark:text-gray-400">
          <li>Inclusive: GST is included in the price. Exclusive: GST is added on top of the price.</li>
          <li>CGST & SGST are for intra-state, IGST for inter-state transactions.</li>
          <li>IGST should be equal to CGST + SGST.</li>
        </ul>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.length === 0 && (
          <div className="col-span-full text-center text-gray-400 text-sm py-8">No GST groups added yet.</div>
        )}
        {groups.map((group, idx) => (
          <div key={idx} className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-5 flex flex-col gap-2 relative group hover:shadow-2xl transition-all">
            <div className="flex items-center justify-between mb-2">
              <span className="text-lg font-semibold text-gray-800 dark:text-gray-100">{group.name}</span>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${group.type === 'inclusive' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{group.type.charAt(0).toUpperCase() + group.type.slice(1)}</span>
                <button className="p-2 rounded-full hover:bg-yellow-100 text-yellow-700 transition-all" title="Edit" onClick={() => handleEdit(idx)}>
                  <FiEdit2 />
                </button>
                <button className="p-2 rounded-full hover:bg-red-100 text-red-700 transition-all" title="Delete" onClick={() => handleDeleteClick(idx)}>
                  <FaTrash />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 text-sm text-gray-700 dark:text-gray-200">
              <span><b>GST:</b> {group.percentage}%</span>
              <span><b>CGST:</b> {group.cgst || 0}%</span>
              <span><b>SGST:</b> {group.sgst || 0}%</span>
              <span><b>IGST:</b> {group.igst || 0}%</span>
            </div>
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-xs text-gray-400">ID: {idx + 1}</div>
          </div>
        ))}
      </div>
      <ModalPopup show={showForm} onClose={() => { setShowForm(false); setEditingGroup(null); setErrors({}); }}>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">{editingGroup !== null ? "Edit GST Group" : "Create GST Group"}</h3>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">Group Name</label>
            <input className={`border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all`} name="name" value={form.name} onChange={handleChange} required />
            {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">GST Type</label>
              <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all" name="type" value={form.type} onChange={handleChange}>
                <option value="inclusive">Inclusive</option>
                <option value="exclusive">Exclusive</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">GST Percentage</label>
              <input className={`border ${errors.percentage ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all`} name="percentage" type="number" step="0.1" min="0" max="100" value={form.percentage} onChange={handleChange} required />
              {errors.percentage && <div className="text-xs text-red-500 mt-1">{errors.percentage}</div>}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">CGST (%)</label>
              <input className={`border ${errors.cgst ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all`} name="cgst" type="number" step="0.1" min="0" max="100" value={form.cgst} onChange={handleChange} />
              {errors.cgst && <div className="text-xs text-red-500 mt-1">{errors.cgst}</div>}
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">SGST (%)</label>
              <input className={`border ${errors.sgst ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all`} name="sgst" type="number" step="0.1" min="0" max="100" value={form.sgst} onChange={handleChange} />
              {errors.sgst && <div className="text-xs text-red-500 mt-1">{errors.sgst}</div>}
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">IGST (%)</label>
              <input className={`border ${errors.igst ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all`} name="igst" type="number" step="0.1" min="0" max="100" value={form.igst} onChange={handleChange} />
              {errors.igst && <div className="text-xs text-red-500 mt-1">{errors.igst}</div>}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all" onClick={() => { setShowForm(false); setEditingGroup(null); setErrors({}); }}>Cancel</button>
            <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm transition-all">{editingGroup !== null ? "Update" : "Create"}</button>
          </div>
        </form>
      </ModalPopup>
      <ModalPopup show={showDeleteConfirm} onClose={cancelDelete}>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">Delete GST Group</h3>
          <p className="text-gray-600 dark:text-gray-300">
            Are you sure you want to delete the GST group <span className="font-semibold">"{deleteIndex !== null ? groups[deleteIndex]?.name : ''}"</span>?
          </p>
          <p className="text-sm text-red-600 dark:text-red-400">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button 
              type="button" 
              className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all" 
              onClick={cancelDelete}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
              onClick={confirmDelete}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </ModalPopup>
    </div>
  );
};

export default GST;
